import { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth, apiFetch } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    User as UserIcon,
    Package,
    Shield,
    LogOut,
    ChevronRight,
    MapPin,
    Truck,
    CreditCard,
    Loader2,
    AlertCircle,
    ExternalLink,
} from "lucide-react";
import { formatMXN, formatMXN2 } from "@/lib/format";
import { toast } from "sonner";

const ORDER_STATUS_META = {
    pending_payment: { label: "Pendiente de pago", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
    paid: { label: "Pagado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    label_generated: { label: "Guía generada", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    shipped: { label: "Enviado", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    delivered: { label: "Entregado", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    cancelled: { label: "Cancelado", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
    refunded: { label: "Reembolsado", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    out_of_stock: { label: "Sin stock", cls: "bg-red-50 text-red-700 border-red-200" },
};

function StatusBadge({ status }) {
    const m = ORDER_STATUS_META[status] || ORDER_STATUS_META.pending_payment;
    return (
        <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border ${m.cls}`}>
            {m.label}
        </span>
    );
}

export default function CustomerAccountPage() {
    const { user, isAuthenticated, isLoading, logout } = useAuth();

    if (isLoading) return null;
    if (!isAuthenticated)
        return <Navigate to="/login" state={{ from: "/cuenta" }} replace />;

    return (
        <div data-testid="customer-account" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <header className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary flex items-center gap-1.5">
                        <UserIcon className="w-3.5 h-3.5" /> Mi cuenta
                    </p>
                    <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
                        Hola, {user.name?.split(" ")[0] || "tú"}
                    </h1>
                    <p className="mt-2 text-sm text-zinc-500">{user.email}</p>
                </div>
                <Button
                    onClick={logout}
                    variant="outline"
                    className="border-zinc-200"
                    data-testid="account-logout"
                >
                    <LogOut className="w-4 h-4 mr-1.5" /> Cerrar sesión
                </Button>
            </header>

            <Tabs defaultValue="orders" className="mt-10">
                <TabsList className="bg-transparent border-b border-zinc-200 rounded-none w-full justify-start h-auto p-0 gap-6">
                    <TabsTrigger
                        value="orders"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        <Package className="w-3.5 h-3.5 mr-1.5" /> Mis pedidos
                    </TabsTrigger>
                    <TabsTrigger
                        value="profile"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        <UserIcon className="w-3.5 h-3.5 mr-1.5" /> Mis datos
                    </TabsTrigger>
                    <TabsTrigger
                        value="security"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        <Shield className="w-3.5 h-3.5 mr-1.5" /> Seguridad
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="orders" className="pt-6">
                    <OrdersTab />
                </TabsContent>
                <TabsContent value="profile" className="pt-6">
                    <ProfileTab />
                </TabsContent>
                <TabsContent value="security" className="pt-6">
                    <SecurityTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}

// ============== TAB: Pedidos ==============
function OrdersTab() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data: r } = await apiFetch.get("/me/orders", {
                params: { limit: 100 },
            });
            setData(r);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (loading)
        return (
            <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="h-20 rounded-xl bg-zinc-100 animate-pulse" />
                ))}
            </div>
        );

    if (data.items.length === 0)
        return (
            <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center">
                <div className="mx-auto w-12 h-12 grid place-items-center rounded-full bg-zinc-100">
                    <Package className="w-5 h-5 text-zinc-400" />
                </div>
                <p className="mt-4 text-sm text-zinc-500">Aún no tienes pedidos.</p>
                <p className="mt-1 text-sm text-zinc-500">
                    Cuando compres en una tienda, tus pedidos aparecerán aquí.
                </p>
            </div>
        );

    return (
        <>
            <p className="text-xs text-zinc-500 mb-3">{data.total} pedido(s)</p>
            <ul className="rounded-xl border border-zinc-200 bg-white divide-y divide-zinc-100 overflow-hidden">
                {data.items.map((o) => {
                    const itemCount = (o.items || []).reduce((a, i) => a + (i.quantity || 0), 0);
                    return (
                        <li key={o.id}>
                            <button
                                onClick={() => setDetail(o)}
                                data-testid={`account-order-${o.orderNumber}`}
                                className="w-full text-left flex items-center gap-4 px-4 sm:px-5 py-4 hover:bg-zinc-50 transition"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-sm font-semibold text-zinc-900">{o.orderNumber}</span>
                                        <StatusBadge status={o.status} />
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        {o.storeName} · {itemCount} artículo{itemCount !== 1 ? "s" : ""}
                                        {" · "}
                                        {o.deliveryMethod === "pickup" ? "Entrega acordada" : o.shipping?.carrier || "—"}
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-sm font-semibold text-zinc-900">{formatMXN2(o.amounts?.total)}</div>
                                    <div className="text-[11px] text-zinc-400">
                                        {o.createdAt ? new Date(o.createdAt).toLocaleDateString("es-MX") : ""}
                                    </div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-zinc-300 shrink-0" />
                            </button>
                        </li>
                    );
                })}
            </ul>
            <OrderDetailDialog order={detail} onClose={() => setDetail(null)} />
        </>
    );
}

function OrderDetailDialog({ order, onClose }) {
    if (!order) return null;
    const a = order.shippingAddress || {};
    const sh = order.shipping || {};
    const amounts = order.amounts || {};
    const isPickup = order.deliveryMethod === "pickup";

    return (
        <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-mono flex items-center gap-2">
                        {order.orderNumber}
                        <StatusBadge status={order.status} />
                    </DialogTitle>
                    <DialogDescription>
                        En {order.storeName}
                        {order.createdAt && ` · ${new Date(order.createdAt).toLocaleDateString("es-MX")}`}
                    </DialogDescription>
                </DialogHeader>

                <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Productos</h3>
                    <ul className="space-y-2">
                        {(order.items || []).map((it, i) => (
                            <li key={i} className="flex gap-3 text-sm">
                                <div className="w-12 h-12 rounded-md bg-zinc-100 overflow-hidden shrink-0">
                                    {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-zinc-900 line-clamp-2">{it.productName}</div>
                                    <div className="text-xs text-zinc-500">
                                        {formatMXN(it.price)} × {it.quantity}
                                    </div>
                                </div>
                                <div className="text-zinc-900 font-medium shrink-0">{formatMXN(it.lineTotal)}</div>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="pt-3 border-t border-zinc-100 text-sm space-y-1">
                    <Row label="Productos" value={formatMXN2(amounts.products)} />
                    <Row
                        label={isPickup ? "Entrega" : `Envío (${sh.carrier || "—"})`}
                        value={isPickup ? "Acordada con el vendedor" : formatMXN2(amounts.shipping)}
                    />
                    <Row label="Total" value={formatMXN2(amounts.total)} bold />
                </section>

                <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                        {isPickup ? <MapPin className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
                        {isPickup ? "Entrega" : "Envío"}
                    </h3>
                    {isPickup ? (
                        <p className="text-sm text-zinc-700">
                            El vendedor te contactará para coordinar la entrega.
                        </p>
                    ) : (
                        <div className="text-sm text-zinc-700 space-y-0.5">
                            <div>
                                {a.firstName} {a.lastName}
                            </div>
                            <div>
                                {a.street} {a.exteriorNumber}
                                {a.interiorNumber ? ` int. ${a.interiorNumber}` : ""}
                            </div>
                            <div>{a.neighborhood}</div>
                            <div>
                                {a.city}, {a.state}, CP {a.postalCode}
                            </div>
                            {sh.guideNumber && (
                                <div className="mt-2 pt-2 border-t border-zinc-100 space-y-1">
                                    <div className="text-xs text-zinc-500">
                                        Guía: <span className="font-mono text-zinc-800">{sh.guideNumber}</span>
                                    </div>
                                    {sh.guidePdfUrl && (
                                        <a
                                            href={sh.guidePdfUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline"
                                        >
                                            Ver guía <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                    {sh.trackingUrl && (
                                        <a
                                            href={sh.trackingUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block text-xs text-primary hover:underline"
                                        >
                                            Rastrear envío ↗
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </section>

                <section className="text-xs text-zinc-500">
                    Si tienes alguna duda sobre este pedido, contacta directamente a{" "}
                    <Link to={`/${order.storeSlug}`} className="text-primary font-medium hover:underline">
                        {order.storeName}
                    </Link>
                    .
                </section>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cerrar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex items-center justify-between">
            <span className={bold ? "font-semibold text-zinc-900" : "text-zinc-500"}>{label}</span>
            <span className={bold ? "font-semibold text-zinc-900" : "text-zinc-700"}>{value}</span>
        </div>
    );
}

// ============== TAB: Datos personales ==============
function ProfileTab() {
    const { user, refresh } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        if (name.trim().length < 2) {
            toast.error("El nombre debe tener al menos 2 caracteres");
            return;
        }
        setSaving(true);
        try {
            await apiFetch.put("/auth/me", { name: name.trim() });
            if (refresh) await refresh();
            toast.success("Datos actualizados");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={save}
            className="bg-white rounded-2xl border border-zinc-200 p-6 max-w-xl space-y-4"
        >
            <div className="space-y-1.5">
                <Label htmlFor="acc-name">Nombre completo</Label>
                <Input
                    id="acc-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    data-testid="account-name-input"
                    className="h-11"
                />
            </div>
            <div className="space-y-1.5">
                <Label htmlFor="acc-email">Correo</Label>
                <Input
                    id="acc-email"
                    value={user?.email || ""}
                    disabled
                    className="h-11 bg-zinc-50"
                />
                <p className="text-xs text-zinc-500">
                    Por seguridad, el correo no se puede cambiar desde aquí. Si necesitas hacerlo, contacta a soporte.
                </p>
            </div>
            <div className="pt-2">
                <Button
                    type="submit"
                    disabled={saving}
                    data-testid="account-name-save"
                    className="bg-primary hover:bg-primary-hover text-primary-foreground"
                >
                    {saving ? "Guardando…" : "Guardar cambios"}
                </Button>
            </div>
        </form>
    );
}

// ============== TAB: Seguridad ==============
function SecurityTab() {
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [revoking, setRevoking] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) {
            toast.error("La nueva contraseña debe tener al menos 8 caracteres");
            return;
        }
        if (next !== confirm) {
            toast.error("La confirmación no coincide");
            return;
        }
        setSaving(true);
        try {
            await apiFetch.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Contraseña actualizada");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo cambiar la contraseña");
        } finally {
            setSaving(false);
        }
    };

    const revokeAll = async () => {
        if (
            !window.confirm(
                "Esto cerrará todas tus sesiones en todos los dispositivos. ¿Continuar?"
            )
        )
            return;
        setRevoking(true);
        try {
            await apiFetch.post("/auth/sessions/revoke-all");
            toast.success("Sesiones cerradas. Vuelve a iniciar sesión.");
            navigate("/login", { replace: true });
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo cerrar las sesiones");
        } finally {
            setRevoking(false);
        }
    };

    return (
        <div className="max-w-xl space-y-6">
            <form
                onSubmit={submit}
                className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4"
            >
                <h2 className="text-sm font-semibold text-zinc-900">Cambiar contraseña</h2>
                <div className="space-y-1.5">
                    <Label htmlFor="acc-pass-current">Contraseña actual</Label>
                    <Input
                        id="acc-pass-current"
                        type="password"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        autoComplete="current-password"
                        required
                        data-testid="account-pass-current"
                        className="h-11"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="acc-pass-new">Nueva contraseña</Label>
                    <Input
                        id="acc-pass-new"
                        type="password"
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        autoComplete="new-password"
                        minLength={8}
                        required
                        data-testid="account-pass-new"
                        className="h-11"
                    />
                    <p className="text-xs text-zinc-500">Mínimo 8 caracteres.</p>
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="acc-pass-confirm">Confirma la nueva contraseña</Label>
                    <Input
                        id="acc-pass-confirm"
                        type="password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        autoComplete="new-password"
                        minLength={8}
                        required
                        data-testid="account-pass-confirm"
                        className="h-11"
                    />
                </div>
                <div className="pt-2">
                    <Button
                        type="submit"
                        disabled={saving}
                        data-testid="account-pass-save"
                        className="bg-primary hover:bg-primary-hover text-primary-foreground"
                    >
                        {saving ? "Cambiando…" : "Cambiar contraseña"}
                    </Button>
                </div>
            </form>

            <section className="bg-white rounded-2xl border border-zinc-200 p-6">
                <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" /> Cerrar todas mis sesiones
                </h2>
                <p className="mt-2 text-xs text-zinc-500">
                    Si crees que alguien más tiene acceso a tu cuenta, cierra todas las sesiones activas. Tendrás que iniciar sesión de nuevo.
                </p>
                <Button
                    onClick={revokeAll}
                    disabled={revoking}
                    variant="outline"
                    className="mt-4 border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="account-revoke-all"
                >
                    {revoking ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Cerrando…
                        </>
                    ) : (
                        "Cerrar todas mis sesiones"
                    )}
                </Button>
            </section>
        </div>
    );
}
